import { useField } from 'formik'
import React from 'react'
import { Form, InputGroup } from 'react-bootstrap'
import styled from 'styled-components'
import { FormGroup } from 'components'
import Icon from 'components/Icon'
import InfoTooltip from 'components/InfoTooltip'
import { _, colors } from 'utils'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useDispatch } from 'react-redux'
import { showNotification } from 'features/notifications'
import { TextFieldProps } from '../types'

const S = {
    FormGroup: styled(FormGroup)`
        position: relative;
        & textarea::placeholder {
            font-size: 14px;
            color: ${colors.grayTitle};
            opacity: 0.5;
        }
    `,
    InfoTooltip: styled(InfoTooltip)<{ isTextArea?: boolean }>`
        position: absolute;
        top: 33px;
        right: ${({ isTextArea }) => (isTextArea ? 28 : 20)}px;
    `,
    InfoIcon: styled(Icon)`
        cursor: pointer;
    `,
    AppendText: styled.span<{ valueLength: number }>`
        position: absolute;
        top: 39px;
        left: ${({ valueLength }) => valueLength*8 + 26}px;
        font-size: 14px;
        color: ${colors.grayTitle};
    `,
    PrependText: styled(InputGroup.Text)`
        font-size: 14px;
        color: ${colors.grayTitle};
    `,
    LabelWrapper: styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 26px;
    `,
    Status: styled.div<{ isActiveStatus: boolean }>`
        display: flex;
        align-items: center;
        margin-bottom: 2px;
        text-transform: uppercase;
        font-weight: bold;
        font-size: 14px;
        color: ${({ isActiveStatus }) => (isActiveStatus ? colors.primary : colors.iconOpacity(0.5))};
        opacity: ${({ isActiveStatus }) => (isActiveStatus ? 1 : 0.75)};
    `,
    Copy: styled(Icon)`
        position: absolute;
        top: 33px;
        right: 18px;
        transform: scaleY(1.1);
    `,
    HighLightHelperText: styled.span`
        font-size: 12px;
        color: ${colors.icon};
    `
}

const TextField = ({
    name,
    label,
    helperText,
    isRequired,
    placeholder = '',
    isDisabled,
    isReadOnly,
    showInfoIcon,
    type = 'text',
    prependText,
    appendText,
    isActiveStatus,
    status = '',
    statusHelperText,
    isTextArea,
    row,
    textToCopy,
    highLightHelperText,
    ...props
}: TextFieldProps) => {
    const [
        field,
        meta,
    ] = useField({ name })
    const { error, touched } = meta
    const dispatch = useDispatch()
    const showCopiedMessage = () => {
        dispatch(showNotification({
            body: _('notification.copied'),
            type: 'success',
            displayDuration: 1500
        }))
    }

    return (
        <S.FormGroup
            controlId={`${field.name}-text-field`}
            {...props}
        >
            <S.LabelWrapper>
                <Form.Label className={isDisabled ? 'disabled-label' : ''}>
                    {label}
                    {isRequired && <span>&nbsp;*</span>}
                </Form.Label>
                {status && (
                    <S.Status isActiveStatus={isActiveStatus}>
                        {status}
                        <InfoTooltip size={16} text={statusHelperText} />
                    </S.Status>
                )}
            </S.LabelWrapper>
            <InputGroup>
                {prependText && (
                    <InputGroup.Prepend>
                        <S.PrependText>{prependText}</S.PrependText>
                    </InputGroup.Prepend>
                )}
                {isTextArea ? (
                    <Form.Control
                        placeholder={placeholder}
                        isInvalid={touched && !!error}
                        disabled={isDisabled}
                        readOnly={isReadOnly}
                        type={type}
                        as="textarea"
                        rows={row || 8}
                        {...field}
                    />
                ) : (
                    <Form.Control
                        placeholder={placeholder}
                        isInvalid={touched && !!error}
                        disabled={isDisabled}
                        readOnly={isReadOnly}
                        type={type}
                        {...field}
                    />
                )}
            </InputGroup>
            {showInfoIcon && <S.InfoTooltip size={22} text={helperText} isTextArea={isTextArea} />}

            {appendText && !!field.value && (
                <S.AppendText valueLength={field.value.toString().length}>{appendText}</S.AppendText>
            )}

            {
                textToCopy && (
                    <CopyToClipboard text={textToCopy} onCopy={showCopiedMessage}>
                        <S.Copy onClick={() => null} icon="clipboard" size={20} />
                    </CopyToClipboard>
                )
            }

            {!(touched && error) && !showInfoIcon && <Form.Text muted>{helperText}</Form.Text>}
            {highLightHelperText && (
                <S.HighLightHelperText className="flex-align-center">
                    <Icon icon="info" size={14} className="mb-3px mr-1" />
                    {highLightHelperText}
                </S.HighLightHelperText>
            )}

            {touched && error && (
                <small className="text-danger invalid-field">
                    {_(error)}
                </small>
            )}
        </S.FormGroup>
    )
}

export default TextField
